import { h, defineComponent, ref, watch } from 'vue';
import { n, t } from '@/ui/i18n/i18n';
import { box, column, level, loader, pagination, sortableNumericColumn } from '@/ui/components';
import {
  groupedQuestionList,
  groupedScoresListExportUrl,
  SupportedExportFormat,
} from '@/services/api/resources/analytics';
import { emptyGroupedQuestionList, GroupedQuestion, GroupedQuestionList } from '@/entities/analytics';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import * as qs from '@/ui/queryState';
import { download, enumToSelectOption } from '@/ui/helpers/uiHelper';
import { hasFeature, hasPermission, Permissions, user } from '@/ui/auth/auth';
import { QuestionGroupBy } from '@/services/api/resources/analytics/types';
import { AnalyticsTable } from '@/ui/components/table/AnalyticsTable';
import {
  categoryFilter,
  checklistFilter,
  dateRangeFilter,
  filterContainer,
  groupFilter,
  inspectorFilter,
  questionTitleFilter,
  responseFilter,
  sectionFilter,
  entityItemFilter,
  entityFolderFilter,
} from '@/ui/components/filters';
import { dropdown } from '@/ui/components/controls/Dropdown';
import { SortDirection } from '@/ui/types';
import { exportDropdown } from '@/ui/components/dropdown/ExportDropdown';
import { renderForbiddenPage } from '../../error/ForbiddenPage';
import { decodeGroup, mergeEntityItems } from '../codec';
import { questionGroupBy } from '../dictionary';
import { useQueryFiltering } from './useQueryFilters';
import { useFilters } from './useFilters';
import { scoresDetailPageLink } from './ScoresDetailPage';
import { renderAnalyticsTeaserPage } from '../../teasers/AnalyticsTeaserPage';
import { ScoresCharts } from '@/ui/pages/analytics/Scores/ScoresCharts';

export const ScoresPage = defineComponent({
  name: 'ScoresPage',
  setup() {
    if (!hasFeature('analytics')) {
      return renderAnalyticsTeaserPage;
    }

    if (!hasPermission(Permissions.VIEW_ALL_ANALYTICS)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);

    // state stored in query string
    const { page, perPage, resetPagination } = usePagination();
    const { sort, dir } = useSorting('group', SortDirection.Ascending);
    const groupBy = qs.stringEnum<QuestionGroupBy>(
      'groupBy',
      Object.values(QuestionGroupBy),
      QuestionGroupBy.EntityItem,
      false,
    );
    const {
      start,
      end,
      group,
      entityItem,
      entityFolder,
      inspector,
      category,
      checklist,
      questionTitle,
      response,
      inspection,
      section,
    } = useQueryFiltering();

    // get list task
    const query = () => ({
      timezone: user.value.timezone,
      page: page.value,
      perPage: perPage.value,
      sort: sort.value,
      start: start.value,
      dir: dir.value,
      end: end.value,
      entityItem: mergeEntityItems(entityFolder.value, entityItem.value),
      group: group.value,
      inspector: inspector.value,
      category: category.value,
      checklist: checklist.value,
      questionTitle: questionTitle.value,
      response: response.value,
      inspection: inspection.value,
      section: section.value,
      //todo: what is the difference with NegativeResponses? if only in these filters - why copying the whole page?
      question: [],
      hasScore: true,
      excludeNegativeResponseCount: false,
    });
    const list = ref<GroupedQuestionList>(emptyGroupedQuestionList);
    const getList = useTask(
      () => groupedQuestionList(groupBy.value, query()),
      userList => {
        list.value = userList;
        initialLoading.value = false;
      },
    );
    watch([
      page,
      perPage,
      group,
      entityItem,
      entityFolder,
      inspector,
      category,
      checklist,
      questionTitle,
      response,
      inspection,
      section,
      start,
      end,
      groupBy,
      sort,
      dir,
    ], getList.run);

    // export task
    const { run: exportData } = useTask(
      (format: SupportedExportFormat | undefined) => groupedScoresListExportUrl(
        groupBy.value,
        query(),
        format as SupportedExportFormat,
        user.value.locale,
      ),
      download,
    );

    // get filters task
    const {
      getFilters,
      entityItems,
      entityFolders,
      groups,
      users,
      categories,
      checklists,
      sections,
    } = useFilters();

    return function render() {
      return h('div', [
        h('div', [
          h('div', { class: 'columns legacy-wrapper' }, [
            filterContainer([
              loader('form', getFilters, [
                dateRangeFilter(start, end, resetPagination),
                entityItemFilter(entityItem, entityItems.value, resetPagination),
                entityFolderFilter(entityFolder, entityFolders.value),
                groupFilter(group, groups.value, resetPagination),
                inspectorFilter(inspector, users.value, resetPagination),
                categoryFilter(category, categories.value, resetPagination),
                checklistFilter(checklist, checklists.value, resetPagination),
                sectionFilter(section, sections.value, resetPagination),
                questionTitleFilter(questionTitle, resetPagination),
                responseFilter(response, resetPagination),
              ]),
            ]),
            h('div', { class: 'column' }, [
              h(ScoresCharts, {
                class: 'legacy',
                props: {
                  query: query(),
                  scorePercentage: list.value.meta.scorePercentage,
                },
              }),
              box([
                loader('div', getList, [
                  level(
                    [
                      //pageTitle(t('analytics.scores')),
                      dropdown<QuestionGroupBy>(
                        {
                          value: groupBy.value,
                          options: Object
                            .values(QuestionGroupBy)
                            .map(enumToSelectOption(questionGroupBy)),
                          disabled: false,
                        },
                        (value: QuestionGroupBy) => qs.updateMultiple(() => {
                          resetPagination();
                          groupBy.value = value;
                        }),
                      ),
                    ],
                    [exportDropdown(exportData)],
                  ),
                  h(AnalyticsTable, {
                    props: {
                      list: list.value.data,
                      sort: sort.value,
                      dir: dir.value,
                      //use client side pagination if no backend pagination
                      paginated: (list.value.data.length > 0 && !list.value.meta.total),
                      perPage: perPage.value,
                      backendPagination: !!list.value.meta.total,
                    },
                    scopedSlots: {
                      default: () => [
                        column({
                          // this customKey property is needed because BTable uses the label
                          // as the primary identifier when refreshing columns, and when column
                          // labels change, it keeps adding new columns
                          customKey: 'group',
                          field: 'group',
                          label: questionGroupBy(groupBy.value),
                          sortable: true,
                        }, {
                          default: ({ row }: { row: GroupedQuestion }) => [
                            scoresDetailPageLink(
                              decodeGroup(row.group),
                              groupBy.value,
                              row.group,
                              {
                                start: start.value,
                                end: end.value,
                                entityItem: entityItem.value,
                                group: group.value,
                                inspector: inspector.value,
                                category: category.value,
                                checklist: checklist.value,
                                question: [],
                                questionTitle: questionTitle.value,
                                response: response.value,
                                inspection: inspection.value,
                                section: section.value,
                              },
                            ),
                          ],
                        }),
                        sortableNumericColumn({
                          field: 'score',
                          label: t('analytics.score-archived'),
                        }, {
                          default: ({ row }: { row: GroupedQuestion }) => n(row.score, 'analyticsInteger'),
                        }),
                        sortableNumericColumn({
                          field: 'maxScore',
                          label: t('analytics.score-potential'),
                        }, {
                          default: ({ row }: { row: GroupedQuestion }) => n(row.maxScore, 'analyticsInteger'),
                        }),
                        sortableNumericColumn({
                          field: 'scorePercentage',
                          label: t('analytics.score-average'),
                        }, {
                          default: ({ row }: { row: GroupedQuestion }) => n(row.scorePercentage, 'analyticsPercentage'),
                        }),
                      ],
                    },
                    on: {
                      sort: (field: string, direction: SortDirection) => qs.updateMultiple(() => {
                        resetPagination();
                        sort.value = field;
                        dir.value = direction;
                      }),
                    },
                  }),
                  (list.value.meta.total)
                    ? pagination({
                      current: page.value,
                      total: list.value.meta.total,
                      perPage: perPage.value,
                    })
                    : null,
                ], !initialLoading.value),
              ]),
            ]),
          ]),
        ]),
      ]);
    };
  },
});
